// @ts-nocheck
import { motion } from 'framer-motion';
import ShimmerButton from './ShimmerButton';
import { UserAvatar } from './UserAvatar';
import { analytics, AnalyticsEvents } from '../utils/analytics';

interface WelcomeBackPageProps {
    firstName?: string;
    isUpgraded?: boolean;
    onStartTrial?: () => void;
    onDownloadApp?: () => void;
    onLogout?: () => void;
    onUpgrade?: () => void;
}

/**
 * Full-page "Welcome Back" for users who already have a paired device.
 * Shows header with logo + UserAvatar, no wizard steps.
 */
export default function WelcomeBackPage({
    firstName,
    isUpgraded = false,
    onStartTrial,
    onDownloadApp,
    onLogout,
    onUpgrade,
}: WelcomeBackPageProps) {
    const handleDownloadApp = () => {
        // Track app download click
        analytics.capture(AnalyticsEvents.APP_DOWNLOAD_CLICKED, {
            location: 'welcome_back_page',
            variant: 'device_already_paired',
        });
        if (onDownloadApp) {
            onDownloadApp();
        }
    };

    const handleStartTrial = () => {
        if (onStartTrial) {
            onStartTrial();
        }
    };

    const steps = [
        {
            emoji: '✅',
            label: 'Art Displaying',
            description: 'Your art is now displaying on your screen',
        },
        {
            emoji: '📱',
            label: 'Get the App',
            description: 'Download the app to control and customize',
        },
        {
            emoji: '✨',
            label: 'Explore',
            description: 'Explore more content and features',
        },
    ];

    return (
        <div className="relative z-10 min-h-screen flex flex-col w-full">
            {/* Header with Niio Logo and UserAvatar - NO step indicators */}
            <motion.div
                className="w-full px-6 pt-8 pb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="mx-auto max-w-2xl">
                    <div className="relative flex items-center justify-center h-14">
                        {/* Niio Logo in center */}
                        <motion.div
                            className="absolute left-1/2 flex justify-center items-center z-10"
                            initial={{ opacity: 0, y: -10, x: '-50%' }}
                            animate={{ opacity: 1, y: 0, x: '-50%' }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                        >
                            <motion.div
                                className="relative"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 1,
                                    delay: 0.1,
                                    ease: 'easeOut',
                                }}
                            >
                                {/* Purple glow background */}
                                <motion.div
                                    className="absolute inset-0 bg-purple-500/30 rounded-full blur-2xl"
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{
                                        scale: [0.8, 1, 1.05, 1],
                                        opacity: [0, 0.3, 0.4, 0.3],
                                    }}
                                    transition={{
                                        duration: 2,
                                        delay: 0.2,
                                        repeat: Infinity,
                                        repeatType: 'reverse',
                                        ease: 'easeInOut',
                                    }}
                                    style={{
                                        transform: 'translate(-50%, -50%)',
                                        top: '50%',
                                        left: '50%',
                                        width: '120%',
                                        height: '120%',
                                    }}
                                />
                                {/* Logo */}
                                <img
                                    src="/niio_logo_white.png"
                                    alt="Niio"
                                    className="relative z-10 w-32 h-12 object-contain object-center"
                                    style={{
                                        filter: 'drop-shadow(0 4px 20px rgba(139, 92, 246, 0.4)) drop-shadow(0 2px 10px rgba(139, 92, 246, 0.3))',
                                    }}
                                />
                            </motion.div>
                        </motion.div>

                        {/* User Avatar on the right */}
                        <div className="absolute right-0 flex items-center z-20">
                            <UserAvatar
                                onLogout={onLogout}
                                onUpgrade={onUpgrade}
                            />
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-20 w-full">
                <motion.div
                    className="w-full max-w-md mx-auto"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <div className="text-center">
                        {/* Success Icon */}
                        <motion.div
                            className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                                type: 'spring',
                                duration: 0.6,
                                delay: 0.2,
                            }}
                        >
                            <svg
                                className="w-8 h-8 text-green-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </motion.div>

                        {/* Title */}
                        <motion.h3
                            className="text-2xl font-bold text-white mb-3"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            Welcome back{firstName ? `, ${firstName}` : ''}!
                        </motion.h3>

                        {/* Description */}
                        <motion.p
                            className="text-gray-400 mb-6"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                        >
                            Your device is already connected and displaying art.
                        </motion.p>

                        {/* Steps Section */}
                        <motion.div
                            className="bg-gray-800/50 rounded-lg p-4 mb-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <p className="text-sm text-gray-300 mb-2 font-semibold">
                                What's Next:
                            </p>
                            <ol className="text-sm text-gray-400 text-left space-y-2">
                                {steps.map((step, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start"
                                    >
                                        <span className="mr-2">
                                            {step.emoji}
                                        </span>
                                        <div>
                                            <span className="font-medium text-gray-300">
                                                {step.label}
                                            </span>
                                            {' - '}
                                            <span className="text-gray-400">
                                                {step.description}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </motion.div>

                        {/* Action Buttons */}
                        <motion.div
                            className="space-y-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <ShimmerButton
                                onClick={handleDownloadApp}
                                className="w-full"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    Download
                                    <img
                                        src="/niio_logo_white.png"
                                        alt="NIIO"
                                        className="h-4 w-auto inline-block"
                                        style={{ filter: 'brightness(2)' }}
                                    />
                                    App
                                </span>
                            </ShimmerButton>

                            {!isUpgraded && (
                                <button
                                    onClick={handleStartTrial}
                                    className="w-full py-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/50 rounded-lg text-white font-medium hover:from-purple-600/30 hover:to-pink-600/30 transition-all duration-200"
                                >
                                    Start Your Free Trial
                                </button>
                            )}
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
